/* ============================================================
   US-Parts24 · R567 — Diagnose-Anfrage: Stufen, VIN-Zellen, Schnellwahl.

   Arbeitet NEBEN usparts.js, nicht an seiner Stelle: VIN entschlüsseln
   (decodeVin), Fahrzeugkarte, Garage, Absenden per fetch und Erfolgsbox
   bleiben dort. Hier nur:
     · Stufe 02 geht auf, sobald ein Fahrzeug da ist (17-stellige VIN,
       Fahrzeugkarte sichtbar oder Fahrzeug von Hand eingetippt),
     · Stufe 03, sobald ein Teil da ist (≥ 3 Zeichen oder Teilenummer),
     · einmal offen bleibt offen (nichts springt beim Korrigieren weg),
     · Enter in 01/02 springt zur nächsten Stufe statt halb abzusenden,
     · die VIN füllt 17 Zellen, deren Maß zur Feldbreite gerechnet wird.
   Ohne dieses Skript sind alle Felder offen (Formular bleibt vollständig).
   ============================================================ */
(() => {
  'use strict';
  const VIN_OK = /^[A-HJ-NPR-Z0-9]{17}$/;
  const norm = (v) => (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/I/g, '1').replace(/[OQ]/g, '0');
  const leinwand = document.createElement('canvas').getContext('2d');

  function starten(dx) {
    if (!dx || dx.dataset.dxLaeuft) return;
    dx.dataset.dxLaeuft = '1';

    const form = dx.closest('form') || dx;
    const stufen = [...dx.querySelectorAll('[data-dx-stufe]')];
    const lauf = [...dx.querySelectorAll('[data-dx-lauf]')];
    const vin = dx.querySelector('[data-dx-vin]');
    const vinBox = vin && vin.closest('.usp-dx__vin');
    const zaehler = dx.querySelector('[data-dx-zaehler]');
    const karte = dx.querySelector('[data-usp-vin-card]');
    const manuell = [...dx.querySelectorAll('[data-dx-manuell]')];
    const teil = dx.querySelector('[data-dx-teil]');
    const tnr = dx.querySelector('[data-dx-tnr]');
    const chips = [...dx.querySelectorAll('[data-dx-chip]')];
    let offen = 1;

    const stufe = (n) => stufen.find((s) => Number(s.dataset.dxStufe) === n);
    const zu = (s) => { if (!s) return; s.setAttribute('data-dx-zu', ''); s.inert = true; };

    dx.classList.add('is-dx');
    /* Der Beispieltext passt nicht in 17 Zellen — die leeren Zellen zeigen das
       Format selbst, der Hinweis darunter sagt, wo die VIN steht. */
    if (vin) { vin.dataset.dxPlatzhalter = vin.placeholder; vin.placeholder = ''; }
    stufen.forEach((s) => { if (Number(s.dataset.dxStufe) > 1) zu(s); });

    function fokusIn(n) {
      const s = stufe(n);
      const f = s && s.querySelector('input:not([type="hidden"]):not([disabled]), textarea');
      if (f) setTimeout(() => { try { f.focus({ preventScroll: false }); } catch (_) { f.focus(); } }, 80);
    }

    function oeffnen(n, fokus) {
      for (let i = offen + 1; i <= n; i++) {
        const s = stufe(i);
        if (!s) continue;
        s.removeAttribute('data-dx-zu');
        s.inert = false;
        s.classList.add('is-neu');
        setTimeout(() => s.classList.remove('is-neu'), 1100);
      }
      if (n > offen) offen = n;
      if (fokus) fokusIn(n);
    }

    const vinZeichen = () => (vin ? norm(vin.value).length : 0);
    const fahrzeugOk = () =>
      (vin && VIN_OK.test(norm(vin.value))) ||
      (karte && !karte.hidden) ||
      manuell.some((m) => m.value.trim().length >= 3);
    const teilOk = () => (teil && teil.value.trim().length >= 3) || (tnr && tnr.value.trim().length >= 4);

    function anzeigen() {
      const erfuellt = [fahrzeugOk(), teilOk()];
      lauf.forEach((li) => {
        const n = Number(li.dataset.dxLauf);
        const fertig = n < 3 && erfuellt[n - 1];
        const jetzt = !fertig && (n === 1 || erfuellt.slice(0, n - 1).every(Boolean));
        li.classList.toggle('is-fertig', fertig);
        li.classList.toggle('is-jetzt', jetzt);
      });
      if (vin) {
        const n = Math.min(17, vinZeichen());
        vin.style.setProperty('--dx-n', String(n));
        if (vinBox) {
          vinBox.classList.toggle('is-voll', n === 17 && VIN_OK.test(norm(vin.value)));
          const warn = karte && !karte.hidden && karte.classList.contains('is-warn');
          vinBox.classList.toggle('is-falsch', Boolean(warn));
        }
        if (zaehler) {
          zaehler.textContent = n + '/17';
          zaehler.classList.toggle('is-ok', n === 17);
          zaehler.classList.toggle('is-typing', n > 0 && n < 17);
        }
      }
      if (teil) {
        const drin = teil.value.toLowerCase();
        chips.forEach((c) => c.classList.toggle('is-an', drin.includes(c.dataset.dxChip.toLowerCase())));
      }
    }

    function pruefen() {
      if (fahrzeugOk()) oeffnen(2);
      if (offen >= 2 && teilOk()) oeffnen(3);
      anzeigen();
    }

    /* 17 Zellen: Zellbreite aus der Feldbreite, Schrift so, dass ein Zeichen
       mittig in seiner Zelle steht. Gemessen wird mit der echten Schrift. */
    function zellenMessen() {
      if (!vin || !vin.clientWidth) return;
      const cs = getComputedStyle(vin);
      const pad = 12;
      const innen = vin.clientWidth - 2 * pad;
      const pitch = innen / 17.5;
      const fs = Math.max(16, Math.min(22, pitch * 0.8));
      leinwand.font = `600 ${fs}px ${cs.fontFamily}`;
      const ch = leinwand.measureText('0').width || fs * 0.6;
      const ls = Math.max(0, pitch - ch);
      vin.style.setProperty('--dx-pitch', pitch.toFixed(2) + 'px');
      vin.style.setProperty('--dx-fs', fs.toFixed(1) + 'px');
      vin.style.setProperty('--dx-ls', ls.toFixed(2) + 'px');
      vin.style.setProperty('--dx-pad', pad + 'px');
      vin.style.setProperty('--dx-einzug', Math.max(0, (pitch - 4 - ch) / 2).toFixed(2) + 'px');
    }

    function zuruecksetzen() {
      offen = 1;
      stufen.forEach((s) => { if (Number(s.dataset.dxStufe) > 1) zu(s); });
      dx.classList.remove('is-ohne-vin');
      anzeigen();
    }

    /* Ereignisse */
    form.addEventListener('input', pruefen);
    form.addEventListener('change', pruefen);
    form.addEventListener('focusin', pruefen);
    form.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || e.isComposing || (e.target && e.target.tagName === 'TEXTAREA')) return;
      const s = e.target.closest && e.target.closest('[data-dx-stufe]');
      if (!s) return;
      const n = Number(s.dataset.dxStufe);
      if (n >= 3) return; // in 03 darf Enter absenden
      e.preventDefault();
      oeffnen(n + 1, true);
      anzeigen();
    });

    /* Pflichtfeld in einer noch zugeklappten Stufe (E-Mail in 03): Wird das
       Formular auf einem anderen Weg abgeschickt (Garage, requestSubmit der
       VIN-Wache), bricht der Browser sonst stumm ab — „invalid form control
       is not focusable". Dann die Stufen bis dorthin öffnen und hinspringen.
       Kein eigenes submit-preventDefault: contact[Herkunft] und die VIN-Wache
       (usparts.js) hängen am submit-Ereignis. */
    form.addEventListener('invalid', (e) => {
      const s = e.target && e.target.closest && e.target.closest('[data-dx-stufe]');
      if (!s || !s.hasAttribute('data-dx-zu')) return;
      oeffnen(Number(s.dataset.dxStufe));
      anzeigen();
      const feld = e.target;
      setTimeout(() => { try { feld.focus(); feld.reportValidity(); } catch (_) {} }, 120);
    }, true);

    /* Knöpfe schon beim DRÜCKEN auslösen. Gemessen im Anfrage-Fenster: VIN
       tippen, dann direkt „Bremsen“ klicken → das VIN-Feld verliert den Fokus,
       usparts.js entschlüsselt beim blur erneut und baut die Fahrzeugkarte neu
       → der Knopf rutscht zwischen mousedown und mouseup, der Klick geht ins
       Leere. preventDefault auf mousedown behält den Fokus (kein blur-Umbau),
       click bleibt für Tastatur und Fälle ohne mousedown. */
    function tippbar(el, tun) {
      let erledigt = false;
      el.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        erledigt = true;
        tun();
      });
      el.addEventListener('click', () => {
        if (erledigt) { erledigt = false; return; }
        tun();
      });
    }

    const ohne = dx.querySelector('[data-dx-ohne-vin]');
    if (ohne) tippbar(ohne, () => {
      dx.classList.add('is-ohne-vin');
      const m = manuell[0];
      if (m) setTimeout(() => { try { m.focus(); } catch (_) {} }, 60);
    });

    chips.forEach((c) => tippbar(c, () => {
      if (!teil) return;
      const wort = c.dataset.dxChip;
      const teile = teil.value.split(',').map((x) => x.trim()).filter(Boolean);
      const i = teile.findIndex((x) => x.toLowerCase() === wort.toLowerCase());
      if (i >= 0) teile.splice(i, 1); else teile.push(wort);
      teil.value = teile.join(', ') + (i >= 0 || !teile.length ? '' : ' ');
      teil.dispatchEvent(new Event('input', { bubbles: true }));
      /* Weiterschreiben ist erwünscht („Bremsen vorne, Scheiben + Beläge“) */
      try { teil.focus({ preventScroll: true }); teil.setSelectionRange(teil.value.length, teil.value.length); } catch (_) {}
    }));

    /* usparts.js zeigt die Fahrzeugkarte asynchron (NHTSA) und belegt Felder
       per Skript (Garage, Produktseite) — beides ohne input-Ereignis. */
    if (karte && 'MutationObserver' in window) {
      new MutationObserver(pruefen).observe(karte, { attributes: true, attributeFilter: ['hidden', 'class'] });
    }
    document.addEventListener('click', (e) => {
      if (e.target.closest && e.target.closest('[data-usp-request-open], a[href*="/pages/us-autoteile-teileanfrage"]')) setTimeout(pruefen, 350);
    }, true);
    const reset = form.querySelector('[data-usp-done-reset]');
    if (reset) reset.addEventListener('click', () => setTimeout(zuruecksetzen, 30));

    if (vin && 'ResizeObserver' in window) {
      let breite = 0;
      new ResizeObserver(() => {
        if (vin.clientWidth === breite) return;
        breite = vin.clientWidth;
        requestAnimationFrame(zellenMessen);
      }).observe(vin);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(zellenMessen);
    zellenMessen();
    pruefen();
  }

  /* ⭐ R808 (B6 P2) · Für die Danke-Seite: Teil, Teilenummer, VIN und E-Mail DIESER Anfrage merken — damit sie dort
     freiwillig nur nachfragt, was noch fehlt, und den Nachtrag an dieselbe Anfrage schickt (gleiche Adresse).
     Nur im sessionStorage dieses Tabs (kein Cookie, nichts an Dritte), gelöscht nach Nachtrag oder „Überspringen“. */
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!f || f.tagName !== 'FORM' || !f.querySelector('[data-usp-dx]')) return;
    const wert = (n) => { const el = f.querySelector('[name="contact[' + n + ']"]'); return el ? String(el.value || '').trim() : ''; };
    try {
      sessionStorage.setItem('usp-nachtrag', JSON.stringify({
        teil: wert('Gesuchtes Teil').slice(0, 300), tnr: wert('Teilenummer').slice(0, 40),
        /* So, wie das Cockpit die VIN prüft (groß, ohne Leerzeichen) — Sonderzeichen bleiben, dann fragt die Seite nach. */
        vin: wert('VIN').toUpperCase().replace(/\s+/g, '').slice(0, 24), email: wert('email').slice(0, 120), t: Date.now(),
      }));
    } catch (_) {}
  }, true);

  function alle() { document.querySelectorAll('[data-usp-dx]').forEach(starten); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', alle, { once: true });
  else alle();
  document.addEventListener('shopify:section:load', alle);
})();
